import { useTranslation } from "react-i18next"
import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useState } from "react"
import { LinearProgress } from "@mui/material"

import {
  InvitationDialogProps,
  InvitationEmail,
} from "@/app/settings/club/components/invitation/type"
import { createInvitationStore } from "@/app/settings/club/components/invitation/store"
import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import Chip from "@/components/chip/Chip"
import { useCreateInviteEmailMutation } from "@/hooks/api/useAccountQuery"
import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"
import { emailRegex } from "@/constants/regex"
import { getViteMode } from "@/utils/env"

import "./invitationDialog.scss"

export default function InvitationDialog({
  open,
  onClose,
  onConfirm,
  type: inviteRoleType,
}: InvitationDialogProps) {
  const { t, i18n } = useTranslation("")

  const { setInvitationTotalEmailList, setInvitationErrorEmailList } = createInvitationStore(
    (state) => ({
      setInvitationTotalEmailList: state.setInvitationTotalEmailList,
      setInvitationErrorEmailList: state.setInvitationErrorEmailList,
    }),
  )

  const [isEmailSent, setIsEmailSent] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const [isLongText, setIsLongText] = useState(false)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [emails, setEmails] = useState<InvitationEmail[]>([])
  const [typedEmail, setTypedEmail] = useState<string>("")
  const [progress, setProgress] = useState(0)

  const validation = {
    required: false,
    regex: false,
    duplicate: false,
  }

  const { selectedIdfTeam } = createAccountStore((state) => ({
    selectedIdfTeam: state.selectedIdfTeam,
  }))

  const { mutateAsync } = useCreateInviteEmailMutation()

  const onClickSendEmails = async () => {
    setIsEmailSent(true)
    const totalEmailCount = emails.length
    const progressStep = Math.round((1 / totalEmailCount) * 100)

    const emailPromises = emails.map(({ email }) => {
      const params = {
        idfTeam: selectedIdfTeam as number,
        role: inviteRoleType,
        email,
        language: i18n.language,
        env: getViteMode(),
      }

      return mutateAsync(params)
        .then((result) => {
          setProgress((prev) => Math.min(prev + progressStep, 99))

          return result
        })
        .catch((error) => {
          setProgress((prev) => Math.min(prev + progressStep, 99))

          return { error }
        })
    })

    const sendEmailResults = await Promise.all(emailPromises)
    setProgress(100)

    const errorEmails = sendEmailResults.reduce((target, result, index) => {
      const { error } = result

      if (error) {
        target.push(emails[index].email)
      }

      return target
    }, [] as string[])

    setInvitationTotalEmailList(emails.map(({ email }) => email))
    setInvitationErrorEmailList(errorEmails)
  }

  const onClickDeleteEmail = (deleteIndex: number) => {
    setEmails((prev) => prev.filter((_, index) => index !== deleteIndex))
  }

  const checkDuplicatedEmail = () => {
    setEmails((prev) => {
      return prev.reduce((target, { email, validation }, index) => {
        const isDuplicated = prev.filter(({ email: checkEmail }) => checkEmail === email).length > 1

        if (index <= 29) {
          target.push({
            email,
            isValid: emailRegex.test(email) && !isDuplicated,
            validation: Object.assign({}, validation, {
              duplicate: isDuplicated,
              regex: !emailRegex.test(email),
            }),
          })
        }

        return target
      }, [] as InvitationEmail[])
    })
  }

  const validateEmails = () => {
    const reduceErrorMessages = emails.reduce((errorMessages, email) => {
      const inValidRules = Object.keys(email.validation).filter((rule) => email.validation[rule])

      if (inValidRules.length > 0) {
        const rulesMessages = inValidRules.map((item) =>
          item === "duplicate" ? "" : t("rules.regex", { name: t("email") }),
        )

        errorMessages = errorMessages.concat(rulesMessages)
      }

      return errorMessages
    }, [] as string[])

    setErrorMessages(() => {
      const newErrorMessages = [...new Set(reduceErrorMessages)]
      if (emails.length === 0) {
        newErrorMessages.push(t("rules.required"))
      }

      return newErrorMessages.reverse()
    })
  }

  /**
   * 입력 텍스트를 구분자(공백·탭·줄바꿈·콤마·세미콜론)로 나눠 chip 으로 확정한다.
   * Enter/Space/Comma 키, 붙여넣기, 포커스 아웃 세 경로가 모두 이 함수를 쓴다 (#168).
   */
  const commitEmails = (text: string) => {
    const newEmails = text.split(/[\s,;]+/).reduce((target, email) => {
      if (email) {
        target.push({
          email,
          isValid: true,
          validation: JSON.parse(JSON.stringify(validation)),
        })
      }

      return target
    }, [] as InvitationEmail[])

    if (newEmails.length > 0) {
      setEmails((emails) => [...emails, ...newEmails].slice(0, 30))
      checkDuplicatedEmail()
    }

    setTypedEmail("")
    setIsLongText(false)
  }

  const onChangeTag = (event: ChangeEvent<HTMLTextAreaElement> & Partial<InputEvent>) => {
    setTypedEmail(event.target.value)

    if (!isLongText) {
      setIsLongText(event.target.scrollHeight > event.target.offsetHeight)
    }

    if (event?.data && /[ㄱ-ㅎㅏ-ㅣ가-힣]/g.test(event?.data)) {
      event.target.value = ""
      setTypedEmail("")
    }
  }

  // 붙여넣기: 기존 onChange 는 합성 이벤트에 inputType 이 없어 붙여넣기를 감지하지 못했다.
  // 클립보드 텍스트를 직접 받아 Enter 없이 즉시 chip 으로 만든다 (엑셀 다중 셀 = 탭 구분 포함).
  const onPasteHandler = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = event.clipboardData.getData("text")

    if (!pasted) {
      return
    }

    event.preventDefault()
    commitEmails(`${typedEmail}${pasted}`)
  }

  // 포커스 아웃: Enter 를 누르지 않고 남겨 둔 이메일도 chip 으로 확정한다.
  const onBlurHandler = () => {
    if (typedEmail.trim()) {
      commitEmails(typedEmail)
    }
  }

  const onKeydownHandler = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const keyType = event.code.toLowerCase()

    switch (keyType) {
      case "enter":
      case "space":
      case "comma":
        event.preventDefault()
        commitEmails(typedEmail)
        break

      case "backspace":
        if (!typedEmail || !(event?.target as HTMLTextAreaElement).value) {
          event.preventDefault()

          setEmails(() => [...emails.slice(0, emails.length - 1)])

          checkDuplicatedEmail()
        }

        setIsLongText(false)
        break
      default:
        if (emails.length > 29) {
          event.preventDefault()
          setTypedEmail("")
        }
        break
    }
  }

  const handleClose = () => {
    setIsEmailSent(false)
    setProgress(0)
    setEmails([])
    setTypedEmail("")
    onClose()
  }

  useEffect(() => {
    if (emails.length) {
      checkDuplicatedEmail()
      validateEmails()
    } else {
      setErrorMessages([])
    }
  }, [JSON.stringify(emails)])

  useEffect(() => {
    setIsDisabled(errorMessages.length > 0 || emails.length === 0 || emails.length > 30)
  }, [errorMessages, emails])

  useEffect(() => {
    if (progress >= 100) {
      setTimeout(() => {
        setIsEmailSent(false)
        setProgress(0)
        setEmails([])
        onConfirm()
      }, 1000)
    }
  }, [progress])

  return (
    <Dialog
      title={t(inviteRoleType === AccountTypeKey.Player ? "invitePlayers" : "inviteCoaches")}
      open={open}
      onClose={handleClose}
      onConfirm={onConfirm}
      maxWidth="900px"
      footer={
        <>
          <BasicButton
            type="default-outlined"
            size="md"
            disabled={isEmailSent}
            onClick={handleClose}>
            {t("button.close")}
          </BasicButton>

          <BasicButton
            color="primary"
            type="primary-contained"
            size="md"
            disabled={isDisabled || isEmailSent}
            onClick={onClickSendEmails}>
            {t("button.sendInvitation")}
          </BasicButton>
        </>
      }>
      {isEmailSent && (
        <div>
          <LinearProgress variant="determinate" value={progress} />
        </div>
      )}

      <dl className="InviteDialogCont">
        <dt>{t("emails")}</dt>

        <dd className="email-input">
          {emails.map(({ email, isValid }, index) => (
            <Chip
              label={email}
              type="deletable"
              className={`email-chip ${isValid ? "" : "email-chip-error"}`}
              key={`emailChip_${index}`}
              onClick={() => {
                onClickDeleteEmail(index)
              }}></Chip>
          ))}

          <div className={`textarea-control ${isLongText ? "textarea-control-long" : ""}`}>
            <textarea
              autoFocus
              placeholder={(emails.length === 0 && t("messages.separatedWithComma")) || undefined}
              value={typedEmail}
              disabled={isEmailSent}
              onChange={onChangeTag}
              onKeyDown={onKeydownHandler}
              onPaste={onPasteHandler}
              onBlur={onBlurHandler}
            />
          </div>
        </dd>

        <dd className="dialog-messages">
          {errorMessages.length > 0 && <p className="error-text">{errorMessages[0] || ""}</p>}
          <span className="email-count">
            <em>{emails.length}</em> / 30
          </span>
        </dd>
      </dl>
    </Dialog>
  )
}
